import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { AutoAssignResponse } from '../../domain/dc-schedule.model';
import { AlertService } from '../../services/alert.service';
import { DcRosterService } from '../../services/dc-roster.service';
import { getHttpErrorMessage } from '../../shared/http-error-message.util';

const AUTO_ASSIGN_REPORT_FILENAME = 'auto-assign-report.pdf';

export interface ScheduleAutoAssignResultDialogData {
  response: AutoAssignResponse;
}

@Component({
  selector: 'app-schedule-auto-assign-result-dialog',
  templateUrl: './schedule-auto-assign-result-dialog.component.html',
  styleUrls: ['./schedule-auto-assign-result-dialog.component.css']
})
export class ScheduleAutoAssignResultDialogComponent {
  downloading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: ScheduleAutoAssignResultDialogData,
    private dcRosterService: DcRosterService,
    private alertService: AlertService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  get processedGameCount(): number {
    return this.data.response?.results?.length ?? 0;
  }

  get failedGameCount(): number {
    return (this.data.response?.results ?? []).filter(
      (result) => result.autoAssignStatus === 'FAILED'
    ).length;
  }

  get attemptedAssignmentCount(): number {
    return this.data.response?.eventAssignments?.attempted ?? 0;
  }

  get modifiedAssignmentCount(): number {
    return this.data.response?.eventAssignments?.modified ?? 0;
  }

  async downloadReport(): Promise<void> {
    if (this.downloading) {
      return;
    }
    const gameIds = (this.data.response?.results ?? [])
      .map((result) => result.gameId)
      .filter((gameId) => !!gameId);
    if (gameIds.length === 0) {
      this.alertService.showError(
        'Could not download Auto Assign report: no processed games were returned'
      );
      return;
    }

    try {
      this.downloading = true;
      const report = await firstValueFrom(
        this.dcRosterService.downloadScheduleAutoAssignReport(gameIds)
      );
      this.triggerBrowserDownload(report);
    } catch (error) {
      const errorMessage = await getHttpErrorMessage(error);
      this.alertService.showError(
        'Could not download Auto Assign report: ' + errorMessage
      );
    } finally {
      this.downloading = false;
    }
  }

  private triggerBrowserDownload(report: Blob): void {
    const browserUrl = this.document.defaultView?.URL;
    if (!browserUrl) {
      throw new Error('browser download is unavailable');
    }
    const objectUrl = browserUrl.createObjectURL(report);
    const anchor = this.document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = AUTO_ASSIGN_REPORT_FILENAME;
    anchor.style.display = 'none';
    this.document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      anchor.remove();
      browserUrl.revokeObjectURL(objectUrl);
    }
  }
}
